import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  HStack,
  KdsButton,
  KdsButtonIcon,
  KdsText,
  KdsTextField,
  VStack,
} from "@kazaar-platform/design-system";
import { RiCloseLine, RiDeleteBinLine, RiEditLine } from "@remixicon/react";
import { useTranslate } from "@tolgee/react";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { WorkflowPostponedActivityReadDtoActivityEnum } from "@/config/apiClient";
import { OrderItemStatusEnum, OrderStatusEnum } from "@/config/apiClient.types";
import { useOrderItems, useToasts } from "@/hooks";
import {
  ClientReferenceBlocSchema,
  type InferredClientReferenceBlocSchema,
} from "./ClientReferenceBloc.schema";
import { type ClientReferenceBlocProps } from "./ClientReferenceBloc.types";

export const ClientReferenceBloc = ({
  orderItem,
  isUserCanEditOrder,
}: ClientReferenceBlocProps) => {
  const [isEditing, setIsEditing] = useState(false);

  const { orderId } = useParams();
  const { t } = useTranslate();
  const { add } = useToasts();

  const { useUpdateOrderItem } = useOrderItems();
  const { mutateAsync: updateOrderItem, isPending } = useUpdateOrderItem();

  const methods = useForm<InferredClientReferenceBlocSchema>({
    resolver: zodResolver(ClientReferenceBlocSchema),
    defaultValues: {
      clientReference: orderItem?.clientReference || "",
    },
  });

  const hasAModificationRequest = orderItem.Workflow.PostponedActivities.some(
    (postponedActivity) =>
      postponedActivity.activity ===
      WorkflowPostponedActivityReadDtoActivityEnum.DecideOnModificationRequest
  );

  const handleClientReferenceSubmit = async (
    data: InferredClientReferenceBlocSchema
  ) => {
    if (data.clientReference === orderItem?.clientReference) {
      setIsEditing(false);
      return;
    }

    try {
      await updateOrderItem({
        orderId: orderId ?? "",
        orderItemId: orderItem.id,
        orderItemUpdateCommand: {
          clientReference: data.clientReference ?? "",
          allowEditionOrderItem:
            orderItem?.order.status === OrderItemStatusEnum.IN_PROGRESS,
        },
      });

      add({
        state: "success",
        title: t("orderItemSummary.referenceId.toast.success"),
      });

      setIsEditing(false);
    } catch (error) {
      console.error(error);
      add({
        state: "error",
        title: t("orderItemSummary.referenceId.toast.error"),
      });
    }
  };

  return (
    <form
      onSubmit={(e) =>
        void methods.handleSubmit(handleClientReferenceSubmit)(e)
      }
    >
      <Box rounded="br-sm" bgColor="elevation.first" p="sp-md" w="100%">
        <VStack gap="sp-xs">
          <KdsText as="h2" size="title-sm">
            {t("orderItemSummary.referenceId.title")}
          </KdsText>

          <KdsText color="text-bodyvariant">
            {t("orderItemSummary.referenceId.description")}
          </KdsText>

          <HStack gap="sp-xs" align="center">
            {!isEditing && (
              <>
                <KdsText
                  size="subtitle-md"
                  color={
                    orderItem?.clientReference
                      ? "text-body"
                      : "text-bodyvariant"
                  }
                  isItalic={!orderItem?.clientReference}
                >
                  {orderItem?.clientReference ||
                    t("orderItemSummary.referenceId.noReference")}
                </KdsText>

                {(orderItem.status === OrderStatusEnum.IN_PROGRESS ||
                  (orderItem.status === OrderStatusEnum.TO_STUDY &&
                    !hasAModificationRequest)) && (
                  <>
                    <KdsButtonIcon
                      variant={
                        orderItem?.clientReference ? "outlined" : "contained"
                      }
                      disabled={!isUserCanEditOrder}
                      onPress={() => setIsEditing(!isEditing)}
                      icon={RiEditLine}
                      size="small"
                    />

                    {orderItem?.clientReference && (
                      <KdsButtonIcon
                        onPress={() => {
                          void handleClientReferenceSubmit({
                            clientReference: "",
                          });
                          methods.setValue("clientReference", "");
                        }}
                        icon={RiDeleteBinLine}
                        size="small"
                        color="error"
                        variant="outlined"
                      />
                    )}
                  </>
                )}
              </>
            )}

            {isEditing && (
              <>
                {/* TODO: Replace with ControlledTextField */}
                <Controller
                  {...methods}
                  name="clientReference"
                  render={({ field }) => (
                    <KdsTextField
                      iconRight={RiCloseLine}
                      placeholder={t(
                        "orderItemSummary.referenceId.inputPlaceholder"
                      )}
                      onRightIconPress={() =>
                        methods.setValue("clientReference", "")
                      }
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />

                <KdsButton
                  disabled={!isUserCanEditOrder}
                  type="submit"
                  isLoading={isPending}
                >
                  {t("buttons.record")}
                </KdsButton>

                {orderItem?.clientReference && (
                  <KdsButtonIcon
                    onPress={() => {
                      void handleClientReferenceSubmit({ clientReference: "" });
                      methods.setValue("clientReference", "");
                    }}
                    disabled={!isUserCanEditOrder}
                    icon={RiDeleteBinLine}
                    color="error"
                    variant="outlined"
                  />
                )}
              </>
            )}
          </HStack>
        </VStack>
      </Box>
    </form>
  );
};
